import { Component, Prop, h, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-long-content-type-4';

/**
 * Two-column content section for SignNow brand — article text with sidebar.
 * 8-column main area renders raw HTML body content; 4-column sidebar accepts
 * supplementary slot content (e.g. how-it-works, rating widgets).
 *
 * @product signnow
 * @source dynamiccontent
 * @version 1.0.0
 * @category SignNow
 * @slot heading - <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @slot body - {raw-html} - HTML body content (used when rawHtml prop is not provided)
 * @slot sidebar - Optional sidebar content
 * @example
 *   <sn-long-content-type-4
 *     raw-html="&lt;p&gt;Electronic signatures are legally binding.&lt;/p&gt;"
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2">Electronic Signature Legality</me-heading>
 *     <div slot="sidebar"><sn-how-it-works-type-1></sn-how-it-works-type-1></div>
 *   </sn-long-content-type-4>
 */
@Component({
  tag: 'sn-long-content-type-4',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnLongContentType4 {
  /**
   * Color mode for SignNow theming
   * @default "signnow"
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Raw HTML body content rendered via innerHTML.
   * When provided, takes precedence over the `body` slot.
   * @content
   */
  @Prop() rawHtml?: string;

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <me-section yGap="2xl" yGapLg="5xl" role="region" aria-label="Dynamic Content">
          <me-row>
            <me-column span="12" spanMd="8" class={`${COMPONENT_NAME}__main`}>
              <slot name="heading"></slot>
              {this.rawHtml
                ? <div class={COMPONENT_NAME} innerHTML={this.rawHtml}></div>
                : <slot name="body"></slot>}
            </me-column>
            <me-column span="12" spanMd="4" class={`${COMPONENT_NAME}__sidebar`}>
              <slot name="sidebar"></slot>
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
